import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { ShieldCheck, Lock, KeyRound, ServerCog, ScrollText, Bug } from "lucide-react";

const PILLARS = [
    {
        Icon: Lock,
        h: "Encryption",
        p: "All traffic between you and Ventara is protected by TLS 1.2 or above with modern cipher suites. Data at rest is encrypted using AES-256. Secrets are held in a dedicated key-management service and rotated on a fixed schedule.",
    },
    {
        Icon: KeyRound,
        h: "Access control",
        p: "Access to production systems is role-based, requires SSO and hardware-backed multi-factor authentication, and is reviewed quarterly. Least-privilege is the default; standing access to customer data is limited to a small, named on-call rotation.",
    },
    {
        Icon: ServerCog,
        h: "Infrastructure",
        p: "Ventara runs on hardened cloud infrastructure in providers that hold SOC 2, ISO 27001, and equivalent certifications. Environments are logically isolated by tenant. All infrastructure is defined as code, reviewed on change, and monitored continuously.",
    },
    {
        Icon: ShieldCheck,
        h: "Application security",
        p: "We treat security as part of the design surface, not a late-stage check. Dependencies are scanned continuously, secrets are managed centrally, and every merge to production is reviewed by a second engineer. We run static analysis, software composition analysis, and periodic third-party penetration tests.",
    },
    {
        Icon: ScrollText,
        h: "Governance & compliance",
        p: "We are aligned with the SOC 2 Trust Services Criteria and pursuing formal certification. Our privacy programme is designed to meet the GDPR, UK GDPR, and CCPA where applicable. Data-processing agreements and Standard Contractual Clauses are available on request.",
    },
    {
        Icon: Bug,
        h: "Responsible disclosure",
        p: "If you believe you have found a vulnerability, we would like to hear from you. Email security@iventara.com with a clear description and, if possible, a proof of concept. Please give us reasonable time to remediate before public disclosure. We do not pursue legal action against researchers acting in good faith.",
    },
];

const PRACTICES = [
    {
        n: "01",
        h: "Incident response",
        p: "We maintain a written incident-response plan with defined roles, communication paths, and severity levels. On confirmation of a security incident affecting customer data, we notify affected customers and, where required, supervisory authorities within the statutory window.",
    },
    {
        n: "02",
        h: "Monitoring & logging",
        p: "Application, infrastructure, and access logs are centralized, retained for at least twelve months, and reviewed continuously for anomalies. Alerts are triaged by our on-call engineers, who follow published runbooks.",
    },
    {
        n: "03",
        h: "Backup & recovery",
        p: "Customer data is backed up on an encrypted, redundant schedule. Restoration procedures are tested regularly. Our stated recovery-time objective and recovery-point objective are available under NDA.",
    },
    {
        n: "04",
        h: "Vendor management",
        p: "Every subprocessor is reviewed for its security and privacy posture before onboarding and re-assessed at least annually. A current list of subprocessors is available on request.",
    },
    {
        n: "05",
        h: "People security",
        p: "Every Ventara teammate completes background verification before joining, signs confidentiality obligations, and completes security-and-privacy training on hire and annually thereafter.",
    },
    {
        n: "06",
        h: "Data localisation",
        p: "Where a customer requires their data to remain in a specific region, we honour that requirement using region-pinned deployments. Speak with us to arrange it.",
    },
];

export default function Security() {
    return (
        <Layout title="Security — Ventara" description="How Ventara protects customer data, systems, and people.">
            <PageHero
                eyebrow="Trust · Effective February 2026"
                title={<>Security is the<br />quiet part.</>}
                lead="We build calm systems on top of serious defences. Below is a plain-language overview of how Ventara protects the data you entrust to us."
            />

            {/* Six pillars */}
            <Section className="pb-24">
                <div className="grid md:grid-cols-2 gap-x-12 gap-y-16 border-t border-border pt-16">
                    {PILLARS.map(({ Icon, h, p }, i) => (
                        <Reveal key={h} delay={(i % 2) * 0.05}>
                            <div data-testid={`security-pillar-${i}`}>
                                <div className="brand-gradient-text w-fit">
                                    <Icon className="h-6 w-6" strokeWidth={1.4} />
                                </div>
                                <h2 className="font-serif text-3xl tracking-tight mt-6">{h}</h2>
                                <p className="prose-body mt-4 max-w-md">{p}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Practices */}
            <Section className="pb-24">
                <p className="label mb-6">Operating practices</p>
                <div className="divide-y divide-border border-t border-b border-border max-w-4xl">
                    {PRACTICES.map((s) => (
                        <Reveal key={s.n}>
                            <div className="grid lg:grid-cols-12 gap-8 py-10" data-testid={`security-practice-${s.n}`}>
                                <div className="lg:col-span-2 label pt-2">{s.n}</div>
                                <div className="lg:col-span-10">
                                    <h3 className="font-serif text-2xl tracking-tight">{s.h}</h3>
                                    <p className="prose-body mt-3 max-w-2xl">{s.p}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Contact strip */}
            <Section className="pb-32">
                <div className="border border-border rounded-3xl p-10 sm:p-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                        <p className="label">Report a vulnerability</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight">Write to <span className="brand-gradient-text">security@iventara.com</span></h2>
                        <p className="prose-body mt-6 max-w-2xl">We acknowledge every valid report within two business days, keep you informed as we investigate, and credit you publicly once a fix has shipped — unless you prefer to remain anonymous.</p>
                    </div>
                    <div className="lg:col-span-4 flex justify-end">
                        <a
                            href="mailto:security@iventara.com"
                            data-testid="security-contact-btn"
                            className="brand-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm text-white shadow-[0_10px_40px_-10px_rgba(255,77,166,0.4)] hover:brightness-110 transition"
                        >
                            Send a report →
                        </a>
                    </div>
                </div>
            </Section>
        </Layout>
    );
}
